import React, { useState } from 'react'
import { format } from 'date-fns'
import { pl } from 'date-fns/locale'
import { StickyNote, Trash2, Plus } from 'lucide-react'
import { useNotificationContext } from '../../contexts/NotificationContext'

export interface ServiceNote {
  id: number
  content: string
  created_by: number
  author_name?: string | null
  created_at?: string | null
  can_delete: boolean
}

interface ServiceNotesProps {
  token: string
  serviceId: string
  notes: ServiceNote[]
  // Liczone przez backend dla bieżącego użytkownika — nie powielamy tu reguł.
  canAdd: boolean
  onChange: (notes: ServiceNote[]) => void
}

const MAX_LENGTH = 2000
// Licznik pokazujemy dopiero przy zbliżaniu się do limitu — wcześniej to szum.
const COUNTER_FROM = 1600

async function errorDetail(response: Response, fallback: string): Promise<string> {
  try {
    const data = await response.json()
    if (typeof data.detail === 'string') return data.detail
    if (Array.isArray(data.detail) && data.detail[0]?.msg) return String(data.detail[0].msg)
  } catch {
    // brak JSON-a w odpowiedzi
  }
  return fallback
}

/**
 * Notatki do zlecenia — uwagi z terenu dopisywane w trakcie realizacji
 * (np. „da się wykonać, ale pod warunkiem…"). Trafiają do raportu z realizacji.
 */
const ServiceNotes: React.FC<ServiceNotesProps> = ({ token, serviceId, notes, canAdd, onChange }) => {
  const { showError, showConfirm } = useNotificationContext()
  const [editorOpen, setEditorOpen] = useState(false)
  const [draft, setDraft] = useState('')
  const [saving, setSaving] = useState(false)
  const [deletingId, setDeletingId] = useState<number | null>(null)

  const trimmed = draft.trim()
  const tooLong = trimmed.length > MAX_LENGTH

  const save = async () => {
    if (!trimmed || tooLong) return
    setSaving(true)
    try {
      const response = await fetch(`/api/services/${serviceId}/notes`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: trimmed }),
      })
      if (response.ok) {
        const note: ServiceNote = await response.json()
        onChange([...notes, note])
        setDraft('')
        setEditorOpen(false)
      } else {
        // Treść zostaje w polu — technik nie przepisuje notatki po błędzie.
        showError('Nie zapisano notatki', await errorDetail(response, 'Spróbuj ponownie'))
      }
    } catch {
      showError('Błąd połączenia', 'Notatka nie została zapisana — treść jest nadal w polu')
    } finally {
      setSaving(false)
    }
  }

  const remove = async (note: ServiceNote) => {
    const confirmed = await showConfirm('Usunąć notatkę?', note.content.slice(0, 120))
    if (!confirmed) return
    setDeletingId(note.id)
    try {
      const response = await fetch(`/api/services/${serviceId}/notes/${note.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      })
      if (response.ok) {
        onChange(notes.filter((n) => n.id !== note.id))
      } else {
        showError('Nie usunięto notatki', await errorDetail(response, 'Spróbuj ponownie'))
      }
    } catch {
      showError('Błąd połączenia', 'Nie można połączyć się z serwerem')
    } finally {
      setDeletingId(null)
    }
  }

  return (
    <div className="bg-amber-50 rounded-lg p-6">
      <div className="flex items-center justify-between gap-3 mb-4">
        <h4 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <StickyNote className="w-5 h-5 text-amber-600" aria-hidden />
          Notatki
          {notes.length > 0 && (
            <span className="text-sm font-medium text-amber-800 bg-amber-100 rounded-full px-2 py-0.5">
              {notes.length}
            </span>
          )}
        </h4>
        {canAdd && !editorOpen && (
          <button
            type="button"
            onClick={() => setEditorOpen(true)}
            className="inline-flex items-center gap-1 px-3 py-2 min-h-[44px] text-sm font-medium rounded-md bg-amber-600 hover:bg-amber-700 text-white"
          >
            <Plus className="w-4 h-4" aria-hidden />
            Dodaj notatkę
          </button>
        )}
      </div>

      {editorOpen && (
        <div className="mb-4 bg-white rounded-lg p-3 shadow-sm">
          <label htmlFor="service-note-draft" className="block text-sm font-medium text-gray-700 mb-1">
            Nowa notatka
          </label>
          <textarea
            id="service-note-draft"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={4}
            autoFocus
            placeholder="Np. do wykonania po wyłączeniu wody przez gminę; potrzebna koparka"
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
          />
          <div className="flex items-center justify-between gap-3 mt-2">
            <span className={`text-xs ${tooLong ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
              {trimmed.length >= COUNTER_FROM ? `${trimmed.length} / ${MAX_LENGTH}` : 'Notatka trafi do raportu z realizacji.'}
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => { setEditorOpen(false); setDraft('') }}
                disabled={saving}
                className="px-3 py-2 min-h-[44px] text-sm rounded-md bg-gray-100 hover:bg-gray-200"
              >
                Anuluj
              </button>
              <button
                type="button"
                onClick={save}
                disabled={saving || !trimmed || tooLong}
                className="px-3 py-2 min-h-[44px] text-sm font-medium rounded-md bg-amber-600 hover:bg-amber-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? 'Zapisuję…' : 'Zapisz notatkę'}
              </button>
            </div>
          </div>
        </div>
      )}

      {notes.length === 0 ? (
        <p className="text-sm text-gray-600">Brak notatek.</p>
      ) : (
        <ul className="space-y-2">
          {notes.map((note) => (
            <li key={note.id} className="bg-white rounded p-3 shadow-sm">
              <div className="flex justify-between items-start gap-3">
                <p className="text-xs text-gray-600">
                  <span className="font-medium text-gray-800">{note.author_name || 'Nieznany'}</span>
                  {note.created_at && (
                    <> • {format(new Date(note.created_at), 'dd.MM.yyyy HH:mm', { locale: pl })}</>
                  )}
                </p>
                {note.can_delete && (
                  <button
                    type="button"
                    onClick={() => remove(note)}
                    disabled={deletingId === note.id}
                    aria-label="Usuń notatkę"
                    title="Usuń notatkę"
                    className="inline-flex items-center justify-center min-w-[44px] min-h-[44px] -m-2 text-gray-400 hover:text-red-600 disabled:opacity-50"
                  >
                    <Trash2 className="w-4 h-4" aria-hidden />
                  </button>
                )}
              </div>
              <p className="text-sm text-gray-900 whitespace-pre-wrap mt-1">{note.content}</p>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

export default ServiceNotes
